import { useI18n } from '@/i18n'
import { X } from '@/shared/icons/phosphor'
import { type JSX, Show } from 'solid-js'
import { Dynamic } from 'solid-js/web'
import { Avatar, type AvatarSize, avatarColorIndex } from '../../primitives/Avatar'
import './UserPill.css'

export type UserPillSize = 'sm' | 'md'
export type UserPillVariant = 'soft' | 'outlined' | 'bare'

export interface UserPillProps {
  name: string
  src?: string | null | undefined
  avatar?: JSX.Element | undefined
  meta?: string | undefined
  size?: UserPillSize | undefined
  variant?: UserPillVariant | undefined
  colorIndex?: number | undefined
  href?: string | undefined
  onClick?: (() => void) | undefined
  onRemove?: (() => void) | undefined
  class?: string | undefined
}

const avatarRung: Record<UserPillSize, AvatarSize> = { sm: 'sm', md: 'md' }

export function UserPill(props: UserPillProps) {
  const { t } = useI18n()

  const size = () => props.size ?? 'md'
  const isInteractive = () => props.href !== undefined || props.onClick !== undefined
  const isRemovable = () => props.onRemove !== undefined

  const classes = () =>
    [
      'ds-user-pill',
      `ds-user-pill--${size()}`,
      `ds-user-pill--${props.variant ?? 'soft'}`,
      props.meta ? 'ds-user-pill--with-meta' : '',
      props.avatar ? 'ds-user-pill--custom-avatar' : '',
      props.class,
    ]
      .filter(Boolean)
      .join(' ')

  const interactiveTag = () => (props.href === undefined ? 'button' : 'a')

  const interactiveProps = () => {
    if (props.href !== undefined) {
      return { href: props.href }
    }
    return { type: 'button' as const, onClick: () => props.onClick?.() }
  }

  const rootTag = () => {
    if (isRemovable() || !isInteractive()) {
      return 'span'
    }
    return interactiveTag()
  }

  const identityTag = () => {
    if (isRemovable() && isInteractive()) {
      return interactiveTag()
    }
    return 'span'
  }

  return (
    <Dynamic
      component={rootTag()}
      class={classes()}
      {...(rootTag() === 'span' ? {} : interactiveProps())}
    >
      <Dynamic
        component={identityTag()}
        class="ds-user-pill__identity"
        {...(identityTag() === 'span' ? {} : interactiveProps())}
      >
        <Show
          when={props.avatar}
          fallback={
            <Avatar
              class="ds-user-pill__avatar"
              src={props.src ?? null}
              name={props.name}
              size={avatarRung[size()]}
              colorIndex={props.colorIndex ?? avatarColorIndex(props.name)}
            />
          }
        >
          <span class="ds-user-pill__avatar ds-user-pill__avatar--custom">{props.avatar}</span>
        </Show>
        <span class="ds-user-pill__body">
          <span class="ds-user-pill__name">{props.name}</span>
          <Show when={props.meta}>
            <span class="ds-user-pill__meta">{props.meta}</span>
          </Show>
        </span>
      </Dynamic>
      <Show when={isRemovable()}>
        <button
          type="button"
          class="ds-user-pill__remove"
          aria-label={t('designSystem.userPill.remove', { name: props.name })}
          onClick={() => props.onRemove?.()}
        >
          <X class="w-3 h-3" />
        </button>
      </Show>
    </Dynamic>
  )
}
